import { WINDOW_LIMITS, formatPlace, formatSize, parsePlace, parseSize, type FramedWindowId, type PlaceSize, type PlaceSource, type SizeSource, type WindowPlace } from "../../ui/kit/index.ts";
import { localStore, readSetting, writeSetting, type SettingsStore } from "../settings.ts";

/**
 * The kept places and sizes of the game page's windows (P1.9-T97 and P1.9-T183, ADR-061's amendments "a window can be
 * moved" and "every game window moves and resizes"), as `ChatSizeSetting` holds the chat panel's size: one setting per
 * window and per kind of wish, made from what the store holds as the page first asks for it, read by the window, which
 * follows each change, and changed by the window's title bar and its edges, each kept change written per device under
 * the window's own key (`odyssey.window.<id>.place` and `odyssey.window.<id>.size`).
 */

/**
 * The windows that keep a place and a size: every window that has a frame.
 */
export type WindowId = FramedWindowId;

/**
 * The storage key a window's place is kept under.
 *
 * @param id The window.
 * @returns The key.
 * @example
 * windowPlaceKey("inventory");
 * // "odyssey.window.inventory.place"
 */
export function windowPlaceKey(id: WindowId): string {
  return `odyssey.window.${id}.place`;
}

/**
 * One window's kept wish, a place or a size: what it is now, what the store holds, who follows it, and the one write
 * a kept change makes.
 */
class WindowWishSetting<T> {
  private current: T | null;
  private written: T | null;
  private readonly listeners = new Set<() => void>();

  /**
   * @param key The storage key.
   * @param format Writes a wish as the text to keep; the empty text for none.
   * @param same Whether two wishes, or none, are the same.
   * @param store Where the wish is kept; null for none.
   * @param parse Reads the kept text as a wish, null for anything else.
   */
  constructor(
    private readonly key: string,
    private readonly format: (wish: T | null) => string,
    private readonly same: (a: T | null, b: T | null) => boolean,
    private readonly store: SettingsStore | null,
    parse: (raw: string | null) => T | null,
  ) {
    this.current = readSetting(key, parse, store);
    this.written = this.current;
  }

  /**
   * The wish now; what a page draws of it is the kit's `drawFrame`'s.
   *
   * @returns The wish, the same object until it changes, or null for a window never moved or resized.
   */
  readonly getSnapshot = (): T | null => this.current;

  /**
   * Follows the wish.
   *
   * @param listener Told after each change.
   * @returns Stops following.
   */
  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * Takes a wish from the window's title bar or edges, which the window shows at once.
   *
   * @param wish The wish, or null to return the window to where or as large as its layout makes it.
   * @param keep Whether to keep it: false while a drag is under way, so the store is written once, as it ends, and
   *   not at all when the wish is the one the store already holds.
   */
  readonly set = (wish: T | null, keep: boolean): void => {
    if (!this.same(wish, this.current)) {
      this.current = wish;
      for (const listener of this.listeners) {
        listener();
      }
    }
    if (keep && !this.same(this.written, this.current)) {
      this.written = this.current;
      writeSetting(this.key, this.format(this.current), this.store);
    }
  };

  /**
   * Forgets the wish and writes the empty text over the key, whatever the key held, so a key holding text no wish
   * reads is cleared too. The window follows at once.
   */
  readonly clear = (): void => {
    this.set(null, false);
    this.written = null;
    writeSetting(this.key, this.format(null), this.store);
  };
}

/**
 * Whether two places are the same.
 *
 * @param a A place, or null for none.
 * @param b Another.
 * @returns True when both are none or both name the same left and top.
 */
function samePlace(a: WindowPlace | null, b: WindowPlace | null): boolean {
  return a === null || b === null ? a === b : a.left === b.left && a.top === b.top;
}

/**
 * Whether two sizes are the same.
 *
 * @param a A size, or null for none.
 * @param b Another.
 * @returns True when both are none or both name the same width and height.
 */
function sameSize(a: PlaceSize | null, b: PlaceSize | null): boolean {
  return a === null || b === null ? a === b : a.width === b.width && a.height === b.height;
}

/**
 * The storage key a window's size is kept under.
 *
 * @param id The window.
 * @returns The key.
 * @example
 * windowSizeKey("inventory");
 * // "odyssey.window.inventory.size"
 */
export function windowSizeKey(id: WindowId): string {
  return `odyssey.window.${id}.size`;
}

/**
 * One window's kept place.
 *
 * @example
 * ```ts
 * const place = new WindowPlaceSetting("inventory");
 * place.set({ left: 120, top: 60 }, true);
 * ```
 */
export class WindowPlaceSetting extends WindowWishSetting<WindowPlace> implements PlaceSource {
  /**
   * @param id The window whose place this is.
   * @param store Where the place is kept; this browser's `localStorage` when omitted, and null for none.
   */
  constructor(id: WindowId, store: SettingsStore | null = localStore()) {
    super(windowPlaceKey(id), formatPlace, samePlace, store, parsePlace);
  }
}

/**
 * One window's kept size, held to the window's limits and its step as it is read.
 *
 * @example
 * ```ts
 * const size = new WindowSizeSetting("inventory");
 * size.set({ width: 325, height: 236 }, true);
 * ```
 */
export class WindowSizeSetting extends WindowWishSetting<PlaceSize> implements SizeSource {
  /**
   * @param id The window whose size this is.
   * @param store Where the size is kept; this browser's `localStorage` when omitted, and null for none.
   */
  constructor(id: WindowId, store: SettingsStore | null = localStore()) {
    super(windowSizeKey(id), formatSize, sameSize, store, (raw) => parseSize(raw, WINDOW_LIMITS[id]));
  }
}

/**
 * The page's settings made so far, one per window.
 */
const PLACES = new Map<WindowId, WindowPlaceSetting>();

/**
 * The page's kept place for a window, made over this browser's `localStorage` the first time it is asked for, so
 * every part of the page that draws by a window's place (the window, a tooltip beside it) follows the same one.
 *
 * @param id The window.
 * @returns Its setting.
 * @example
 * windowPlace("inventory").getSnapshot();
 */
export function windowPlace(id: WindowId): WindowPlaceSetting {
  const held = PLACES.get(id);
  if (held !== undefined) {
    return held;
  }
  const made = new WindowPlaceSetting(id);
  PLACES.set(id, made);
  return made;
}

/**
 * The page's sizes made so far, one per window.
 */
const SIZES = new Map<WindowId, WindowSizeSetting>();

/**
 * The page's kept size for a window, made over this browser's `localStorage` the first time it is asked for, so every
 * part of the page that draws by a window's size (the window, a tooltip beside it) follows the same one.
 *
 * @param id The window.
 * @returns Its setting.
 * @example
 * windowSize("inventory").getSnapshot();
 */
export function windowSize(id: WindowId): WindowSizeSetting {
  const held = SIZES.get(id);
  if (held !== undefined) {
    return held;
  }
  const made = new WindowSizeSetting(id);
  SIZES.set(id, made);
  return made;
}

/**
 * Returns every game window to its layout's default place and size (ADR-061's amendment "every game window moves and
 * resizes", item 12): the empty text is written over every window's kept place and size, the map's float and its
 * pop-out size included, and each open window follows in the same frame. The chat panel's size is not a window's wish
 * and is not touched.
 *
 * @param place Gives a window's kept place; the page's own by default.
 * @param size Gives a window's kept size; the page's own by default.
 */
export function resetWindows(place: (id: WindowId) => { readonly clear: () => void } = windowPlace, size: (id: WindowId) => { readonly clear: () => void } = windowSize): void {
  for (const id of Object.keys(WINDOW_LIMITS) as WindowId[]) {
    place(id).clear();
    size(id).clear();
  }
}
